/**
 * JWT validation utilities
 *
 * Client-side JWT validation for route protection
 */

interface JWTPayload {
  userId: string;
  email: string;
  exp: number; // Expiration timestamp (seconds)
  iat: number; // Issued at timestamp (seconds)
}

/**
 * Parse JWT token without verifying signature
 * (Signature verification happens on backend)
 */
function parseJWT(token: string): JWTPayload {
  try {
    // JWT format: header.payload.signature
    const parts = token.split(".");
    if (parts.length !== 3) {
      throw new Error("Invalid JWT format");
    }

    // Decode payload (base64url)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch (error) {
    throw new Error("Failed to parse JWT");
  }
}

/**
 * Check if JWT is expired
 */
export function isJWTExpired(token: string): boolean {
  try {
    const payload = parseJWT(token);
    const now = Math.floor(Date.now() / 1000); // Current time in seconds
    return payload.exp < now;
  } catch {
    return true; // Treat invalid tokens as expired
  }
}

/**
 * Get time until JWT expires (in seconds)
 * Returns negative number if already expired
 */
function getJWTTimeToExpiry(token: string): number {
  try {
    const payload = parseJWT(token);
    const now = Math.floor(Date.now() / 1000);
    return payload.exp - now;
  } catch {
    return -1;
  }
}

/**
 * Validate JWT token (client-side checks only)
 * Returns true if token is valid and not expired
 */
export function validateJWT(token: string | null | undefined): boolean {
  if (!token) return false;

  try {
    const payload = parseJWT(token);

    // Check required fields
    if (!payload.userId || !payload.email || !payload.exp) {
      return false;
    }

    // Check expiration
    const now = Math.floor(Date.now() / 1000);
    if (payload.exp < now) {
      return false;
    }

    return true;
  } catch {
    return false;
  }
}

/**
 * Extract user ID from JWT token
 */
function getUserIdFromJWT(token: string): string | null {
  try {
    const payload = parseJWT(token);
    return payload.userId || null;
  } catch {
    return null;
  }
}

/**
 * Extract email from JWT token
 */
function getEmailFromJWT(token: string): string | null {
  try {
    const payload = parseJWT(token);
    return payload.email || null;
  } catch {
    return null;
  }
}
